import { setFetchAuthInfoHook, setLogger, setLogoutHook, setRefreshTokenHook, setStorage, type PersistedData } from '@bear-auth/core';
import { createIndexedDBStorage } from '@bear-auth/storage';
import { push } from 'redux-first-history';
import z from 'zod';

import config from '@/config';
import { logger } from '@/config/logger';
import { env } from '@/env';
import { signOut, type RefreshTokenRequestBodyBody } from '@/modules/api/codegen';
import type { BodyType } from '@/modules/api/fetcher/fetcher';
import { getStore } from '@/services/context';

import { authUserSchema, type AuthUser } from '../../schemas/oauth';
import { getDemoUserWithCredentials } from '../../utils/getDemoUser';
import { bearAuthId, demoBearAuthId } from './create';
import { fetchAuthUser } from './fetchAuthUser';

function configureBearAuthLocalStorage(id: string) {
    setStorage<AuthUser>(id, {
        version: 1,
        set: async (id, value) => {
            localStorage.setItem(id, JSON.stringify(value));
        },
        get: async id => {
            const value = localStorage.getItem(id);

            try {
                return JSON.parse(value!) as PersistedData<AuthUser>;
            } catch {
                return undefined;
            }
        },
        remove: async id => {
            localStorage.removeItem(id);
        },
        clear: async id => {
            localStorage.removeItem(id);
        },
    });
}

function configureLogger(id: string) {
    if (env.VITE_PLAYWRIGHT_RUN || !env.VITE_BEAR_AUTH_DEBUG_LOGS) {
        setLogger(id, {
            error: logger.error,
            debug: () => {},
            info: () => {},
        });
    } else {
        setLogger(id, logger);
    }
}

/**
 * Based on the `RefreshTokenResponseResponse` type from the @workspace/api package
 */
const refreshAccessTokenResponse = z.object({
    accessToken: z.string(),
    expiration: z.number(),
});

/**
 * We mustn't use the refreshAccessToken from the @workspace/api package, because it uses the auth interceptor, which will block the API request for refreshing the access token and it will never be refreshed.
 */
async function refreshAccessToken(body: BodyType<RefreshTokenRequestBodyBody>) {
    const response = await fetch(new URL(`${env.VITE_API}/v2/oauth/refresh-token`), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'x-islocalhost': config.isLocalhost ? 'true' : 'false',
        },
        cache: 'no-store',
        body: JSON.stringify(body),
    });

    const data = await response.json();

    return refreshAccessTokenResponse.parse(data);
}

function configureAppBearAuth(id: string) {
    if (env.VITE_PLAYWRIGHT_RUN) {
        configureBearAuthLocalStorage(id);
    } else {
        const storage = createIndexedDBStorage({
            bearAuthId: id,
            authInfo: authUserSchema,
        });
        setStorage<AuthUser>(id, storage);
    }

    configureLogger(id);

    const fetchAuthInfo = setFetchAuthInfoHook<AuthUser>(id, async tokens => {
        return await fetchAuthUser(tokens.accessToken);
    });

    const refreshToken = setRefreshTokenHook<AuthUser>(id, async tokens => {
        const { refreshToken } = tokens;

        if (!refreshToken) {
            throw new Error(`Can't refresh access token without refresh token.`);
        }

        const { accessToken, expiration } = await refreshAccessToken({ refreshToken });

        return {
            refreshToken,
            accessToken: accessToken,
            expiration: new Date(expiration).toISOString(),
        };
    });

    const logout = setLogoutHook<AuthUser>(id, async tokens => {
        await signOut({
            token: tokens.accessToken,
        });
    });

    return {
        fetchAuthInfo,
        refreshToken,
        logout,
    };
}

function configureDemoBearAuth(id: string) {
    configureLogger(id);

    if (env.VITE_PLAYWRIGHT_RUN) {
        configureBearAuthLocalStorage(id);
    }

    const fetchAuthInfo = setFetchAuthInfoHook<AuthUser>(id, async tokens => {
        return getDemoUserWithCredentials(tokens.authInfo?.id).authInfo;
    });

    const logout = setLogoutHook<AuthUser>(id, async () => {
        const { dispatch } = await getStore();

        dispatch(push(config.routes.home));
    });

    return {
        fetchAuthInfo,
        logout,
    };
}

export const bearAuthActions = env.VITE_DEMO ? configureDemoBearAuth(demoBearAuthId) : configureAppBearAuth(bearAuthId);
